import React, { useEffect, useState } from "react"
import { v4 as uuid } from "uuid"

import "./Payments.css"
import { PrimaryButton, Title } from "src/pages/home/room-detail/sections/elements/Elements"
import DescriptionDetail from "src/shared/components/data-display/description-detail/DescriptionDetail"
import { useSelector } from "react-redux"
import { RootState } from "src/store/store"
import { useGetReservacionByIdQuery } from "src/gql/schema"
import { useDate } from "src/shared/hooks/useDate"
import { minus, sum, times } from "src/shared/helpers/calculator"
import { RegisterPay } from "src/pages/reservaciones/inicio/components/RegisterPay/RegisterPay"
import Pago from "src/pages/reservaciones/reservaciones/drawer/asignacion-reservaciones-tab-pago/sections/Pago/Pago"
import { formatCurrency } from "src/shared/hooks/formatCurrency"
import { getCardLabel } from "src/shared/sections/payment/helpers/card"
import { isVisibleCardOrReference } from "src/shared/sections/payment/Payment.helpers"
import { formatText } from "src/shared/hooks/formatTextOpcions"
import { useProfile } from "src/shared/hooks/useProfile"
import { ItemMultiplePayment } from "src/pages/home/room-detail/sections/items/Items"
import useIsColaboradorActive from "src/shared/hooks/useIsColaboradorActive"
import { RoleNames } from "src/shared/hooks/useAuth"
import { useIVA } from "src/shared/hooks/useIVA"
import LabelFlexLines from "src/pages/reservaciones/reservaciones/drawer/asignacion-reservaciones-tab-pago/sections/LabelFlexLines"

const Payments = ({ isNoShow }: { isNoShow: boolean }) => {
    const { reservaSeleccionada } = useSelector((root: RootState) => root.roomDetails.reservada)

    const { diffDays } = useDate()
    const { rolName } = useProfile()
    const { InactiveModal, validateIsColabActive } = useIsColaboradorActive()

    const { data, refetch } = useGetReservacionByIdQuery({
        variables: {
            id: reservaSeleccionada?.reserva_id || "",
        },
    })
    const [reservaActualizada, setReservaActualizada] = useState<any>()
    const [totalPorPagar, setTotalPorPagar] = useState(0)
    const { getIVA } = useIVA()

    useEffect(() => {
        setReservaActualizada(data?.reservas[0])
    }, [data])

    useEffect(() => {
        setTotalPorPagar(
            minus(
                reservaActualizada?.total || 0,
                sum(
                    reservaActualizada?.pagos?.flatMap((p) => {
                        return (
                            p.detalles_pago?.map((detallePago) => {
                                return detallePago?.subtotal || 0
                            }) || [0]
                        )
                    }) || [0]
                )
            )
        )
    }, [reservaActualizada])

    const { UTCStringToLocalDate } = useDate()
    const [isRegistrarPagoModalOpen, setisRegistrarPagoModalOpen] = useState(false)
    const diasAlojamiento =
        diffDays(
            UTCStringToLocalDate(reservaActualizada?.fecha_entrada),
            UTCStringToLocalDate(reservaActualizada?.fecha_salida)
        ) || 1

    const totalEstancia = diasAlojamiento * (reservaActualizada?.tarifa?.costo_habitacion || 0)

    const totalPersonasExtra = times(
        times(reservaActualizada?.personas_extras || 0, reservaActualizada?.tarifa?.costo_persona_extra || 0),
        diasAlojamiento
    )

    const totalExperiencias =
        reservaActualizada?.experiencias_reserva?.reduce((total, e) => total + (e?.total || 0), 0) || 0

    const hasPersonasExtra = (reservaActualizada?.personas_extras || 0) > 0
    const hasExperiencias = totalExperiencias > 0

    const totalImpuestos = getIVA(totalEstancia + totalPersonasExtra + totalExperiencias)

    const hasPendingPayment = totalPorPagar > 0

    const hasAnyExtra = hasPersonasExtra || hasExperiencias
    const hasBothExtras = hasPersonasExtra && hasExperiencias

    let paymentsListHeight = "calc(100dvh - 380px)" 

    if (isNoShow) {
        if (hasBothExtras) {
            paymentsListHeight = "calc(100dvh - 420px)"
        } else if (hasAnyExtra) {
            paymentsListHeight = "calc(100dvh - 400px)"
        }
    } else {
        if (hasBothExtras) {
            paymentsListHeight = hasPendingPayment ? "calc(100dvh - 470px)" : "calc(100dvh - 420px)"
        } else if (hasAnyExtra) {
            paymentsListHeight = hasPendingPayment ? "calc(100dvh - 450px)" : "calc(100dvh - 400px)"
        } else if (hasPendingPayment) {
            paymentsListHeight = "calc(100dvh - 430px)"
        }
    }

    return (
        <div className="tab__reservada__payments__container">
            <div
                className="tab__reservada__payments__list"
                style={{
                    height: paymentsListHeight,
                    overflowY: "auto",
                }}
            >
                <Title>Resumen de compra</Title>

                <DescriptionDetail
                    label="Habitación"
                    value={reservaActualizada?.habitacion.tipo_habitacion?.nombre || ""}
                    amount={
                        (reservaActualizada?.tarifa?.costo_habitacion || 0) *
                        diffDays(
                            UTCStringToLocalDate(reservaActualizada?.fecha_entrada),
                            UTCStringToLocalDate(reservaActualizada?.fecha_salida)
                        )
                    }
                    icon="BedFilled"
                    style={{ width: "100%", padding: "12px 0" }}
                />

                {reservaActualizada?.personas_extras > 0 && (
                    <DescriptionDetail
                        label="Personas extra"
                        value={String(reservaActualizada?.personas_extras || 0)}
                        icon="UserParentFill"
                        style={{ width: "100%", padding: "12px 0" }}
                        amount={times(
                            times(
                                reservaActualizada?.personas_extras || 0,
                                reservaActualizada?.tarifa?.costo_persona_extra || 0
                            ),
                            diffDays(
                                UTCStringToLocalDate(reservaActualizada?.fecha_entrada),
                                UTCStringToLocalDate(reservaActualizada?.fecha_salida)
                            )
                        )}
                    />
                )}

                {!!reservaActualizada?.experiencias_reserva?.length && (
                    <ItemMultiplePayment
                        showAmounts
                        icon="star"
                        label="Experiencias"
                        style={{ width: "100%", padding: "12px 0" }}
                        className="detalle-h-items__multiple--experience"
                        labelClass="detalle-h-items__multiple__text--experience"
                        payments={
                            reservaActualizada?.experiencias_reserva?.map((s) => ({
                                label: s.experiencia?.nombre || "",
                                amount: s.total ?? 0,
                            })) || []
                        }
                    />
                )}

                <Title>Pagos</Title>

                {isRegistrarPagoModalOpen && (
                    <RegisterPay
                        id={reservaActualizada?.reserva_id || ""}
                        onClose={() => {
                            refetch()
                            setisRegistrarPagoModalOpen(false)
                        }}
                    />
                )}

                {reservaActualizada?.pagos?.map((p) =>
                    p?.detalles_pago?.map((detallePago) => (
                        <Pago
                            key={uuid()}
                            date={UTCStringToLocalDate(p.fecha_pago)}
                            isCard={isVisibleCardOrReference(detallePago?.tipo_pago)}
                            payType={formatText(detallePago.tipo_pago)}
                            total={detallePago.subtotal}
                            ultimos_digitos={getCardLabel(detallePago)}
                            parcialPay={detallePago.subtotal !== reservaActualizada?.total}
                            type={detallePago.tipo_pago || ""}
                        />
                    ))
                )}
            </div>

            <div className="room-detail--reservada__tab--payments__footer">
                <div className="tab__reservada__payments__divider" />
                <LabelFlexLines
                    label1={`Estancia (${diasAlojamiento})`}
                    value1={formatCurrency(totalEstancia)}
                    label2={hasPersonasExtra ? `Personas extra (${reservaActualizada?.personas_extras})` : undefined}
                    value2={hasPersonasExtra ? formatCurrency(totalPersonasExtra) : undefined}
                    label3={
                        hasExperiencias
                            ? `Experiencias (${reservaActualizada?.experiencias_reserva?.length})`
                            : undefined
                    }
                    value3={hasExperiencias ? formatCurrency(totalExperiencias) : undefined}
                    style={{ fontWeight: 300, fontSize: "13px" }}
                />

                <LabelFlexLines
                    label1="Impuestos"
                    value1={formatCurrency(totalImpuestos)}
                    style={{
                        marginTop: "-5px",
                        fontWeight: 300,
                        fontSize: "13px",
                    }}
                />

                <div className="tab__reservada__payments__divider" />

                <LabelFlexLines
                    label1="Total de cuenta"
                    value1={formatCurrency(Math.max(0, reservaActualizada?.total || 0))}
                    style={{ fontWeight: 700 }}
                />

                <LabelFlexLines
                    label1="Total pagado"
                    value1={formatCurrency(
                        sum(
                            reservaActualizada?.pagos?.flatMap(
                                (p) => p.detalles_pago?.map((d) => d?.subtotal || 0) || [0]
                            ) || [0]
                        )
                    )}
                />

                <LabelFlexLines
                    label1="Por pagar"
                    value1={formatCurrency(Math.max(0, totalPorPagar))}
                    style={{ fontWeight: 700, fontSize: "20px" }}
                />
                {rolName !== RoleNames.valet && rolName !== RoleNames.monitoreo && rolName !== RoleNames.coordinador && totalPorPagar > 0 && (
                    <PrimaryButton
                        text="Registrar pago"
                        disabled={isNoShow}
                        onClick={validateIsColabActive(() => setisRegistrarPagoModalOpen(true))}
                    />
                )}
            </div>

            {InactiveModal}
        </div>
    )
}

export default Payments
